import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ConfirmationService } from 'primeng/api';
import { Tab, TabList, TabPanel, Tabs } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { ButtonDirective } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ExpiryService } from '../../../../services/expiry.service';
import { ExpiringContract, ExpiringInfection } from '../../../../models/Expiry';

import { AppDatePipe } from '../../../../pipes/app-date.pipe';
/**
 * Home dashboard section that lists expiring contracts and expiring infection
 * responses for which the current user is a responsible person. Items can be
 * dismissed from the list for all users of the organization unit.
 */
@Component({
  selector: 'app-expiring',
  templateUrl: './expiring.component.html',
  styleUrls: ['./expiring.component.scss'],

  imports: [
    TranslatePipe,
    AppDatePipe,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    TableModule,
    ButtonDirective,
    Tooltip
  ]
})
export class ExpiringComponent implements OnInit {
  public contracts: Array<ExpiringContract> = [];
  public infections: Array<ExpiringInfection> = [];
  public activeTab: string = 'contracts';

  constructor(
    private expiryService: ExpiryService,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.expiryService.getExpiringContracts().subscribe({
      next: (result) => {
        this.contracts = result || [];
        if (this.contracts.length === 0 && this.infections.length > 0) {
          this.activeTab = 'infections';
        }
      },
      error: () => {
        this.contracts = [];
      }
    });
    this.expiryService.getExpiringInfections().subscribe({
      next: (result) => {
        this.infections = result || [];
        if (this.contracts.length === 0 && this.infections.length > 0) {
          this.activeTab = 'infections';
        }
      },
      error: () => {
        this.infections = [];
      }
    });
  }

  get hasItems(): boolean {
    return this.contracts.length > 0 || this.infections.length > 0;
  }

  removeContract(contract: ExpiringContract, event: Event): void {
    event.stopPropagation();
    const dismissKey =
      contract.id + ':' + new Date(contract.endDate).toISOString().slice(0, 10);
    this.confirmRemove(() => {
      this.expiryService.dismiss('contract', dismissKey).subscribe(() => {
        this.contracts = this.contracts.filter((c) => c.id !== contract.id);
        if (this.contracts.length === 0 && this.infections.length > 0) {
          this.activeTab = 'infections';
        }
      });
    });
  }

  removeInfection(infection: ExpiringInfection, event: Event): void {
    event.stopPropagation();
    this.confirmRemove(() => {
      this.expiryService.dismiss('infection', infection.key).subscribe(() => {
        this.infections = this.infections.filter(
          (i) => i.key !== infection.key
        );
      });
    });
  }

  onTabChange(event: any): void {
    this.activeTab = String(event || 'contracts');
  }

  private confirmRemove(accept: () => void): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('expiring.confirm-remove'),
      header: this.translateService.instant('expiring.confirm-remove-header'),
      icon: 'pi pi-info-circle',
      acceptLabel: this.translateService.instant('common.button.yes'),
      rejectLabel: this.translateService.instant('common.button.no'),
      accept
    });
  }
}
